import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Dropdown, FormLabel, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../../layouts/PageTitle';
import ListPage from '../../../layouts/page/ListPage';
import MenuConfig from '../../../../constants/MenuConfig';
import FilterAccordion from '../../../components/Common/Filter/FilterAccordion';
import InputDate from '../../../components/Forms/Element/InputDate';
import InputText from '../../../components/Forms/Element/InputText';
import CustomSelect from '../../../components/Forms/Element/CustomSelect';
import ListMetricCard, { ListMetricCards } from '../../../components/Common/ListParts/ListMetricCard';
import ModalPage from '../../../components/Common/Modal/ModalPage';
import CopyButton from '../../../components/Common/CopyButton';
import LoadingCenter from '../../../components/Common/LoadingCenter';
import { ApiError } from '../../../components/Common/ApiError';
import { warnAlert } from '../../../components/alert/defaultAlert';
import SaaSBillingService from '../../../../services/superadmin/SaaSBillingService';
import { SearchOperation } from '../../../../utils/ApiUtils';
import { formatDateTime, formatDateToYYYYMMDD, formatUTCDate } from '../../../../utils/DateUtils';
import { convertDoubleToCurrencyPTBR } from '../../../../utils/ConverterUtils';
import {
    SaaSWebhookColumns, WebhookStatusPill, isSubscriptionEvent, webhookEventLabel, webhookResult
} from './SaaSWebhookColumns';
import './SaaSWebhookList.css';

const WEBHOOKS_PATH = '/saas-billing/webhooks';

/**
 * Abre nos últimos 7 dias: quem acompanha webhook quer ver o que chegou agora, e o mês
 * inteiro escondia o evento de hoje no meio do histórico. No dia 1 o mês corrente estaria
 * praticamente vazio.
 */
const defaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 6);
    return date;
};

/** Payload indentado para leitura; se não for JSON válido (entrega recusada), fica como veio. */
const prettyPayload = (payload) => {
    if (!payload) {
        return '-';
    }
    try {
        return JSON.stringify(JSON.parse(payload), null, 2);
    } catch (error) {
        return payload;
    }
};

/** Linha rótulo/valor do modal de detalhes. */
const DetailField = ({ label, children }) => (
    <Col md={6} className="saas-webhook-details__field">
        <span className="saas-webhook-details__label">{label}</span>
        <div className="saas-webhook-details__value">{children || '-'}</div>
    </Col>
);

const SaaSWebhookList = () => {
    const { t } = useTranslation();
    const [selectedRows, setSelectedRows] = useState([]);

    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(() => new Date());
    const [statusFilter, setStatusFilter] = useState('');
    const [eventFilter, setEventFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');

    const [summary, setSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);

    const [detail, setDetail] = useState(null);
    const [showDetail, setShowDetail] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);

    // Opções vindas das próprias traduções: assim o filtro e a grade mostram o mesmo nome.
    const statusOptions = useMemo(() => ['PROCESSED', 'IGNORED', 'FAILED', 'UNAUTHORIZED', 'RECEIVED']
        .map((value) => ({ value, label: t(`saas_webhook.statuses.${value}`) })), [t]);
    const eventOptions = useMemo(() => Object.keys(t('saas_webhook.events', { returnObjects: true }) || {})
        .map((value) => ({ value, label: webhookEventLabel(value) }))
        .sort((a, b) => a.label.localeCompare(b.label)), [t]);

    /** Traduz os campos do filtro para os filtros que a API entende. */
    const buildFilters = useCallback((start, end, status, event, company) => {
        const activeFilters = [];
        if (start) {
            activeFilters.push({ field: 'createdAt', value: `${formatDateToYYYYMMDD(start)}T00:00:00`, operation: SearchOperation.GREATER_THAN_EQUAL });
        }
        if (end) {
            activeFilters.push({ field: 'createdAt', value: `${formatDateToYYYYMMDD(end)}T23:59:59`, operation: SearchOperation.LESS_THAN_EQUAL });
        }
        if (status) {
            activeFilters.push({ field: 'processingStatus', value: status, operation: SearchOperation.EQUAL });
        }
        if (event) {
            activeFilters.push({ field: 'event', value: event, operation: SearchOperation.EQUAL });
        }
        // Por palavras e sem acento: "emporio" encontra "Empório Barbearia".
        if (company.trim()) {
            activeFilters.push({ field: 'contract.company.name', value: company.trim(), operation: SearchOperation.MATCH_WORDS });
        }
        return activeFilters;
    }, []);

    // Filtros já aplicados na abertura, para a grade e os cards nascerem no mesmo recorte.
    const [filters, setFilters] = useState(() => buildFilters(defaultStartDate(), new Date(), '', '', ''));

    // Os cards contam no backend com os mesmos filtros da grade: a grade pagina, e contar só a
    // página exibida daria totais menores que os reais.
    useEffect(() => {
        setLoadingSummary(true);
        SaaSBillingService.webhookEventsSummary(filters)
            .then(({ data }) => setSummary(data))
            .catch((error) => ApiError(error))
            .finally(() => setLoadingSummary(false));
    }, [filters]);

    const handleSearch = () => {
        setFilters(buildFilters(startDate, endDate, statusFilter, eventFilter, companyFilter));
    };

    // Limpar volta aos últimos 7 dias, e não ao histórico inteiro, pelo mesmo motivo da abertura.
    const handleClearFilters = () => {
        const start = defaultStartDate();
        const end = new Date();
        setStartDate(start);
        setEndDate(end);
        setStatusFilter('');
        setEventFilter('');
        setCompanyFilter('');
        setFilters(buildFilters(start, end, '', '', ''));
    };

    // Uma lista nova com o mesmo conteúdo recarrega a grade e os cards sem mexer no filtro:
    // é o "ver se chegou algo" de quem está acompanhando uma entrega.
    const handleRefresh = () => setFilters((current) => [...current]);

    // A grade não traz o payload; o detalhe busca o evento completo só quando é aberto.
    const handleDetails = () => {
        if (selectedRows.length !== 1) {
            warnAlert(t('saas_webhook.details.select_one'));
            return;
        }
        setDetail(null);
        setShowDetail(true);
        setLoadingDetail(true);
        SaaSBillingService.webhookEvent(selectedRows[0].id)
            .then(({ data }) => setDetail(data))
            .catch((error) => {
                setShowDetail(false);
                ApiError(error);
            })
            .finally(() => setLoadingDetail(false));
    };

    const customButtons = [
        <Dropdown.Item key="details" onClick={handleDetails}>
            <i className="fa fa-eye me-2" /> {t('details')}
        </Dropdown.Item>,
        <Dropdown.Item key="refresh" onClick={handleRefresh}>
            <i className="fa fa-rotate-right me-2" /> {t('reload')}
        </Dropdown.Item>
    ];

    const failures = (summary?.failed || 0) + (summary?.unauthorized || 0);
    const lastReceivedCaption = summary?.lastReceivedAt
        ? t('saas_webhook.cards.last_received', { date: formatDateTime(summary.lastReceivedAt) })
        : t('saas_webhook.cards.none_in_period');

    const detailPayload = useMemo(() => prettyPayload(detail?.payload), [detail]);

    return (
        <>
            <PageTitle activeMenu="Webhooks" motherMenu="Cobrança SaaS" />

            <FilterAccordion title="filters" onSearch={handleSearch} onClear={handleClearFilters} centerButtons={false}>
                {/* lg/md para os filtros empilharem no celular. */}
                <Row className="g-2 align-items-end">
                    <Col lg={2} md={6}>
                        <FormLabel>{t('start_date')}</FormLabel>
                        <InputDate selected={startDate} onChange={setStartDate} />
                    </Col>
                    <Col lg={2} md={6}>
                        <FormLabel>{t('end_date')}</FormLabel>
                        <InputDate selected={endDate} onChange={setEndDate} />
                    </Col>
                    <Col lg={2} md={6}>
                        <FormLabel>{t('status')}</FormLabel>
                        <CustomSelect
                            value={statusOptions.find((option) => option.value === statusFilter) || null}
                            options={statusOptions}
                            onChange={(option) => setStatusFilter(option?.value || '')}
                            placeholder={t('saas_webhook.all_statuses')}
                            isClearable
                        />
                    </Col>
                    <Col lg={3} md={6}>
                        <FormLabel>{t('saas_webhook.event')}</FormLabel>
                        <CustomSelect
                            value={eventOptions.find((option) => option.value === eventFilter) || null}
                            options={eventOptions}
                            onChange={(option) => setEventFilter(option?.value || '')}
                            placeholder={t('saas_webhook.all_events')}
                            isClearable
                        />
                    </Col>
                    <Col lg={3} md={12}>
                        <FormLabel>{t('company')}</FormLabel>
                        <InputText
                            value={companyFilter}
                            onChange={(event) => setCompanyFilter(event.target.value)}
                            placeholder={t('saas_webhook.company_placeholder')}
                        />
                    </Col>
                </Row>
            </FilterAccordion>

            <ListMetricCards columns={4}>
                <ListMetricCard
                    accent="primary"
                    label={t('saas_webhook.cards.received')}
                    value={summary?.total ?? 0}
                    caption={lastReceivedCaption}
                    icon="fas fa-inbox"
                    loading={loadingSummary}
                />
                <ListMetricCard
                    accent="success"
                    label={t('saas_webhook.cards.processed')}
                    value={summary?.processed ?? 0}
                    icon="fas fa-check"
                    loading={loadingSummary}
                />
                <ListMetricCard
                    accent="info"
                    label={t('saas_webhook.cards.ignored')}
                    value={summary?.ignored ?? 0}
                    icon="fas fa-forward"
                    loading={loadingSummary}
                />
                {/* Falha e recusa juntas: as duas significam que o Asaas recebeu erro e pedem ação. */}
                <ListMetricCard
                    accent="danger"
                    label={t('saas_webhook.cards.failures')}
                    value={failures}
                    caption={t('saas_webhook.cards.failures_caption', { failed: summary?.failed || 0, unauthorized: summary?.unauthorized || 0 })}
                    icon="fas fa-triangle-exclamation"
                    loading={loadingSummary}
                />
            </ListMetricCards>

            <ListPage
                showPageTitle={false}
                activeMenu="Webhooks"
                motherMenu="Cobrança SaaS"
                path={WEBHOOKS_PATH}
                route={MenuConfig.superadmin.saas_billing.webhooksRoute}
                columns={SaaSWebhookColumns}
                setSelectedRows={setSelectedRows}
                hideDefaultButtons
                customButtons={customButtons}
                filters={filters}
            />

            <ModalPage
                title={t('saas_webhook.details.title')}
                show={showDetail}
                setShow={setShowDetail}
                size="lg"
                centered
                child={loadingDetail || !detail ? <LoadingCenter /> : (
                    <div className="saas-webhook-details">
                        <div className="saas-webhook-details__header">
                            <div>
                                <h5 className="mb-1">{webhookEventLabel(detail.event)}</h5>
                                <code className="saas-webhook-details__code">{detail.event || '-'}</code>
                            </div>
                            <WebhookStatusPill status={detail.processingStatus} />
                        </div>

                        {/* O resultado vem antes dos campos: é a pergunta que levou o operador a abrir o evento. */}
                        <div className={`saas-webhook-details__result saas-webhook-details__result--${detail.processingStatus?.toLowerCase()}`}>
                            <span className="saas-webhook-details__label">{t('saas_webhook.result')}</span>
                            <div>{webhookResult(detail)}</div>
                            {detail.errorMessage && detail.processingDetail && (
                                <div className="saas-webhook-details__error">
                                    <strong>{t('saas_webhook.details.technical_error')}:</strong> {detail.errorMessage}
                                </div>
                            )}
                        </div>

                        <Row className="g-3 mb-3">
                            <DetailField label={t('company')}>{detail.companyName || t('saas_webhook.details.no_company')}</DetailField>
                            <DetailField label={t('saas_webhook.received_at')}>{detail.createdAt && formatDateTime(detail.createdAt)}</DetailField>
                            <DetailField label={t('value')}>{detail.value != null && convertDoubleToCurrencyPTBR(detail.value)}</DetailField>
                            <DetailField label={t(isSubscriptionEvent(detail.event) ? 'saas_webhook.next_due_date' : 'saas_webhook.due_date')}>
                                {formatUTCDate(detail.dueDate)}
                            </DetailField>
                            <DetailField label={t('saas_webhook.details.billing_type')}>
                                {detail.billingType && t(`saas_webhook.billing_types.${detail.billingType}`, { defaultValue: detail.billingType })}
                            </DetailField>
                            <DetailField label={t('saas_webhook.details.processed_at')}>{detail.processedAt && formatDateTime(detail.processedAt)}</DetailField>
                            <DetailField label={t('saas_webhook.details.payment_id')}>{detail.paymentId}</DetailField>
                            <DetailField label={t('saas_webhook.details.subscription_id')}>{detail.subscriptionId}</DetailField>
                        </Row>

                        {detail.invoiceUrl && (
                            <a className="btn btn-outline-primary btn-sm mb-3" href={detail.invoiceUrl} target="_blank" rel="noopener noreferrer">
                                <i className="fa-solid fa-arrow-up-right-from-square me-2" />{t('saas_webhook.details.open_invoice')}
                            </a>
                        )}

                        <div className="saas-webhook-details__payload-head">
                            <span className="saas-webhook-details__label">{t('saas_webhook.details.payload')}</span>
                            <CopyButton textToCopy={detailPayload} showTooltip={false} />
                        </div>
                        <pre className="saas-webhook-details__payload">{detailPayload}</pre>
                    </div>
                )}
            />
        </>
    );
};

export default SaaSWebhookList;
